/**
 * VehiclePhotoField - Foto de perfil do veículo (miniatura + menu Adicionar/Trocar/Remover)
 *
 * A foto serve apenas para identificação visual (ex.: link externo da vistoria).
 * Armazenada no bucket privado `vehicle-photos` em {tenant_id}/{vehicle_id}/{uuid}.ext;
 * leitura/gravação do caminho sempre via RPC `manage_customer_vehicle_photo`.
 *
 * @version 1.3.0
 * RELEASE: 2026-09-29 v1.3.0 - Opção "Visualizar foto" no menu: abre a imagem ampliada
 *          no ImageLightbox padrão do sistema (zoom), disponível quando há foto.
 * RELEASE: 2026-09-29 v1.2.0 - Layout "avatar" (círculo no cabeçalho do modal de criação/edição)
 *          e modo em espera (`pendingFile`/`onSelectFile`) para o cadastro, quando o veículo
 *          ainda não tem id. Helper `uploadVehiclePhoto` exportado para envio pós-cadastro.
 * RELEASE: 2026-09-29 v1.1.0 - Novo layout "row": linha rótulo+miniatura para uso como
 *          primeira opção da seção Identificação na sidebar direita do modal de veículo.
 *          Layout "thumb" (padrão) mantido para usos antigos.
 * RELEASE: 2026-09-29 v1.0.0 - Criação inicial
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Car, Camera, Trash2, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
// v1.3.0: visualização ampliada da foto reutiliza o lightbox padrão do sistema
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';

const logger = createLogger('VehiclePhotoField');
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

// Chamada RPC tipada de forma solta (função nova ainda fora dos tipos gerados)
const rpc = (args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, 'manage_customer_vehicle_photo', args);

/**
 * v1.2.0 — Envia a foto para o bucket e grava o caminho via RPC (mesma regra do componente).
 * Reutilizado pelo modal de cadastro logo após o veículo receber o id.
 */
export async function uploadVehiclePhoto(vehicleId: string, tenantId: string, file: File) {
  const ext = ALLOWED[file.type];
  if (!ext) throw new Error('Formato inválido. Use JPG, PNG ou WEBP.');
  if (file.size > MAX_BYTES) throw new Error('Arquivo acima de 5 MB.');
  const path = `${tenantId}/${vehicleId}/${crypto.randomUUID()}.${ext}`;
  const { error: upErr } = await supabase.storage.from('vehicle-photos').upload(path, file, { contentType: file.type });
  if (upErr) throw new Error(upErr.message);
  const { error } = await rpc({ p_vehicle_id: vehicleId, p_action: 'set', p_photo_path: path });
  if (error) {
    await supabase.storage.from('vehicle-photos').remove([path]);
    throw new Error(error.message);
  }
}

/** v1.2.0 — Valida formato/tamanho no navegador antes de guardar o arquivo em espera. */
export function validateVehiclePhoto(file: File): string | null {
  if (!ALLOWED[file.type]) return 'Formato inválido. Use JPG, PNG ou WEBP.';
  if (file.size > MAX_BYTES) return 'Arquivo acima de 5 MB.';
  return null;
}

interface Props {
  /** Sem id (cadastro) o componente opera em modo espera (pendingFile/onSelectFile). */
  vehicleId: string | null;
  tenantId: string;
  canEdit?: boolean;
  /**
   * v1.1.0: formato de exibição.
   * - 'thumb' (padrão): apenas a miniatura clicável (usada no header do modal).
   * - 'row': linha "Foto do veículo" + miniatura à direita (usada na sidebar).
   */
  layout?: 'thumb' | 'row' | 'avatar';
  /** v1.2.0 — modo espera: arquivo escolhido ainda não enviado (cadastro). */
  pendingFile?: File | null;
  onSelectFile?: (file: File | null) => void;
  /** v1.2.0 — indica salvamento em andamento no modal (spinner no avatar). */
  saving?: boolean;
}

export function VehiclePhotoField({
  vehicleId, tenantId, canEdit = true, layout = 'thumb', pendingFile = null, onSelectFile, saving = false,
}: Props) {
  const pendingMode = !vehicleId;
  // Preview local do arquivo em espera (liberado ao trocar/desmontar)
  const pendingUrl = useMemo(() => (pendingFile ? URL.createObjectURL(pendingFile) : null), [pendingFile]);
  useEffect(() => () => { if (pendingUrl) URL.revokeObjectURL(pendingUrl); }, [pendingUrl]);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const key = ['vehicle-photo', vehicleId];

  // Busca o caminho e gera URL assinada curta para exibir a miniatura
  const { data: remoteUrl } = useQuery({
    queryKey: key,
    enabled: !pendingMode,
    queryFn: async () => {
      const { data, error } = await rpc({ p_vehicle_id: vehicleId, p_action: 'get' });
      if (error) throw new Error(error.message);
      if (!data) return null;
      const { data: s } = await supabase.storage.from('vehicle-photos').createSignedUrl(String(data), 3600);
      return s?.signedUrl ?? null;
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => uploadVehiclePhoto(vehicleId as string, tenantId, file),
    onSuccess: () => { toast.success('Foto do veículo atualizada'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e: Error) => { logger.error('Falha ao enviar foto', e); toast.error(e.message); },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await rpc({ p_vehicle_id: vehicleId, p_action: 'remove' });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { toast.success('Foto removida'); qc.invalidateQueries({ queryKey: key }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const url = pendingMode ? pendingUrl : remoteUrl;
  const busy = upload.isPending || remove.isPending || saving;
  // v1.3.0 — controle do visualizador ampliado (zoom) da foto
  const [viewerOpen, setViewerOpen] = useState(false);

  /** v1.2.0 — seleção: em espera guarda no pai; com id envia na hora. */
  const handleFile = (f: File) => {
    if (pendingMode) {
      const err = validateVehiclePhoto(f);
      if (err) { toast.error(err); return; }
      onSelectFile?.(f);
    } else upload.mutate(f);
  };
  const handleRemove = () => (pendingMode ? onSelectFile?.(null) : remove.mutate());

  // v1.3.0 — itens de menu compartilhados pelos três layouts (Visualizar/Adicionar/Remover)
  const menuItems = (
    <>
      {url && (
        <DropdownMenuItem onClick={() => setViewerOpen(true)}>
          <Eye className="mr-2 h-4 w-4" /> Visualizar foto
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={() => inputRef.current?.click()}>
        <Camera className="mr-2 h-4 w-4" /> {url ? 'Trocar foto' : 'Adicionar foto'}
      </DropdownMenuItem>
      {url && (
        <DropdownMenuItem onClick={handleRemove} className="text-destructive">
          <Trash2 className="mr-2 h-4 w-4" /> Remover foto
        </DropdownMenuItem>
      )}
    </>
  );

  // v1.3.0 — visualizador ampliado (zoom) da foto, padrão do sistema
  const viewer = url ? (
    <ImageLightbox open={viewerOpen} onClose={() => setViewerOpen(false)} imageUrl={url} caption="Foto do veículo" />
  ) : null;
  const thumb = (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        : url ? <img src={url} alt="Foto do veículo" className="h-full w-full object-cover" />
        : <Car className="h-4 w-4 text-muted-foreground" />}
    </span>
  );

  // Input de arquivo compartilhado pelos dois layouts
  const fileInput = (
    <input
      ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden"
      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
    />
  );

  // ===== v1.2.0: layout "avatar" — círculo no cabeçalho do modal de criação/edição =====
  if (layout === 'avatar') {
    const avatar = (
      <span className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-primary/20 bg-primary/10">
        {busy ? <Loader2 className="h-5 w-5 animate-spin text-primary" />
          : url ? <img src={url} alt="Foto do veículo" className="h-full w-full object-cover" />
          : <Car className="h-5 w-5 text-primary" />}
      </span>
    );
    if (!canEdit) return avatar;
    return (
      <>
        {fileInput}
        {viewer}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Foto do veículo" disabled={busy} className="group relative shrink-0 rounded-full">
              {avatar}
              {/* Selo de câmera convidando ao clique */}
              <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground">
                <Camera className="h-2.5 w-2.5" />
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">{menuItems}</DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }

  // ===== v1.1.0: layout "row" — primeira opção da seção Identificação (sidebar) =====
  if (layout === 'row') {
    const row = (
      <div className="flex items-center gap-2 py-1.5 w-full">
        <Camera className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs text-muted-foreground shrink-0">Foto do veículo</span>
        <div className="ml-auto">{thumb}</div>
      </div>
    );
    if (!canEdit) return <>{row}{viewer}</>;

    return (
      <>
        {fileInput}
        {viewer}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* Linha inteira clicável: abre o menu Visualizar/Adicionar/Trocar/Remover */}
            <button type="button" aria-label="Foto do veículo" disabled={busy} className="w-full text-left">
              {row}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">{menuItems}</DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }

  // ===== Layout "thumb" (padrão) — apenas a miniatura =====
  if (!canEdit) return <>{thumb}{viewer}</>;

  return (
    <>
      {fileInput}
      {viewer}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Foto do veículo" disabled={busy}>{thumb}</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">{menuItems}</DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
